import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export const FEEDBACK_MODE_STORAGE_KEY = 'feedback-mode';

interface FeedbackModeContextType {
  active: boolean;
  setActive: (active: boolean) => void;
  toggle: () => void;
}

const FeedbackModeContext = createContext<FeedbackModeContextType>({
  active: false,
  setActive: () => {},
  toggle: () => {},
});

function persist(active: boolean) {
  try {
    if (active) {
      window.sessionStorage.setItem(FEEDBACK_MODE_STORAGE_KEY, '1');
    } else {
      window.sessionStorage.removeItem(FEEDBACK_MODE_STORAGE_KEY);
    }
  } catch {
    // Storage can be unavailable (private mode, quota); the mode still works for this page.
  }
}

export const FeedbackModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [active, setActiveState] = useState(false);

  // Read after mount so the server render and the first client render agree.
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(FEEDBACK_MODE_STORAGE_KEY) === '1') {
        setActiveState(true);
      }
    } catch {
      // Storage unavailable: start with the mode off.
    }
  }, []);

  const setActive = useCallback((next: boolean) => {
    persist(next);
    setActiveState(next);
  }, []);

  const toggle = useCallback(() => {
    setActiveState((prev) => {
      persist(!prev);
      return !prev;
    });
  }, []);

  const value = useMemo(
    () => ({ active, setActive, toggle }),
    [active, setActive, toggle]
  );

  return (
    <FeedbackModeContext.Provider value={value}>
      {children}
    </FeedbackModeContext.Provider>
  );
};

export function useFeedbackMode() {
  return useContext(FeedbackModeContext);
}
